<script setup lang="ts">
import type { Ref } from "vue"
import { toTypedSchema } from "@vee-validate/zod"
import { useForm } from "vee-validate"
import { ref, watchEffect } from "vue"
import { z } from "zod"
import { useGetAlbum, useUpdateAlbum } from "@/api"
import { Button, Datepicker, FileInput, Form, FormSection, GooglePlaceInput, Icon, Input, Switch, Textarea } from "@/components/ui"
import { useRouteParam, useTranslation } from "@/composables"
import { ValidationException } from "@/http"
import { Album, AlbumSettings } from "@/models"
import { useEventVaultStore } from "@/stores/eventVaultStore"
import { locationValidation } from "@/utils"

const emit = defineEmits<{
  success: [album: Album]
  error: [err?: unknown]
}>()

const albumId = useRouteParam("albumId", "/event/[id]/album/[albumId]/edit")
const eventStore = useEventVaultStore()

const { t } = useTranslation()

const validationErrors: Ref<Record<string, any>> = ref({})
function clearValidationErrors(key?: string) {
  if (key) {
    delete validationErrors.value[key]
  }
  else {
    validationErrors.value = {}
  }
}

const { data: existingAlbum, isLoading: isLoadingAlbum } = useGetAlbum(eventStore.eventVaultId, albumId, { include: ["settings", "zip"] })

const validationSchema = toTypedSchema(
  z.object({
    title: z
      .string()
      .min(1, t("album_settings.general.validation.title_required"))
      .max(40, t("album_settings.general.validation.title_max")),
    date: z.date({ required_error: t("album_settings.general.validation.date_required") }),
    description: z.optional(z.string().max(1000, t("album_settings.general.validation.description_max"))),
    coverImage: z.optional(
      z.any().refine(file => file instanceof File, t("album_settings.general.validation.file_type")),
    ).nullable(),
    location: locationValidation(),
    enableUploads: z.boolean(),
  }),
)

const { errors, handleSubmit, setFieldValue } = useForm({
  validationSchema,
  initialValues: {
    title: "",
    description: "",
    location: undefined,
    enableUploads: false,
  },
})

watchEffect(async () => {
  if (existingAlbum.value) {
    initialiseFormValues(existingAlbum.value)
  }
})

function initialiseFormValues(album: Album) {
  setFieldValue("title", album.title)
  setFieldValue("description", album.description)
  setFieldValue("date", album.date)

  if (album.location) {
    setFieldValue("location", album.location)
  }

  if (album?.settings) {
    setFieldValue("enableUploads", !album?.settings?.disableUploads)
    // setFieldValue("enableGallery", album?.settings?.sharePix ?? false)
  }
}

const { isPending, mutate } = useUpdateAlbum()

const onSubmit = handleSubmit((values) => {
  if (!eventStore.eventVaultId) {
    return
  }

  const settings = new AlbumSettings({
    disableUploads: !values.enableUploads,
    // sharePix: values.enableGallery,
  })

  const album: Album = new Album({
    ...existingAlbum.value,
    id: albumId.value,
    title: values.title,
    date: values.date,
    description: values.description,
    coverImage: values.coverImage,
    coverImageUrl: values.coverImage === null ? undefined : existingAlbum.value?.coverImageUrl,
    location: values.location,
    settings,
  })

  mutate({ album, eventVaultId: eventStore.eventVaultId }, {
    onSuccess: (album: Album) => emit("success", album),
    onError: (e: unknown) => {
      if (e instanceof ValidationException) {
        validationErrors.value = e.validationErrors
      }

      emit("error", e)
    },
    onSettled() {
      window.scrollTo({ top: 0, behavior: "smooth" })
    },
  })
})
</script>

<template>
  <Transition name="gpx-fade" mode="out-in">
    <div v-if="isLoadingAlbum" class="flex flex-col gap-6 py-6">
      <div>
        <div class="gpx-skeleton w-48 h-4 mb-2" />
        <div class="gpx-skeleton w-full h-12" />
      </div>
      <div>
        <div class="gpx-skeleton w-48 h-4 mb-2" />
        <div class="gpx-skeleton w-full h-12" />
      </div>
      <div>
        <div class="gpx-skeleton w-48 h-4 mb-2" />
        <div class="gpx-skeleton w-full h-12" />
      </div>
      <div>
        <div class="gpx-skeleton w-48 h-4 mb-2" />
        <div class="gpx-skeleton w-full h-12" />
      </div>
    </div>
    <Form v-else @submit="onSubmit">
      <FormSection :title="t('album_settings.general.details_title')" :description="t('album_settings.general.details_description')" icon="FolderSettings">
        <Input
          :label="t('album_settings.general.title_label')" name="title"
          :error="validationErrors.Title?.length ? validationErrors.Title[0] : ''"
        />
        <Datepicker
          id="album-event-date" name="date" :label="t('album_settings.general.date_label')"
          :error="validationErrors.AlbumDate?.length ? validationErrors.AlbumDate[0] : ''"
          @change="clearValidationErrors('AlbumDate')"
        />
        <GooglePlaceInput
          id="location" name="location.displayAddress"
          :label="t('album_settings.general.location_label')" :error="errors.location"
          @update:location="(loc) => setFieldValue('location', loc)" @error="emit('error')"
        />
        <Textarea
          :label="t('album_settings.general.description_label')" name="description"
          :error="validationErrors.Description?.length ? validationErrors.Description[0] : ''"
        />

        <Switch variant="icon" name="enableUploads" :label="t('album_settings.general.enable_uploads_label')" />
      </FormSection>

      <FormSection :title="t('album_settings.general.cover_image_title')" :description="t('album_settings.general.image_hint')" icon="Images">
        <FileInput
          name="coverImage" accept="image/jpeg,image/png,image/gif,image/webp"
          :preview="existingAlbum?.coverImageUrl ?? ''" preview-type="image"
          :label="t('album_settings.general.cover_label')"
        />
      </FormSection>

      <template #actions>
        <div class="flex gap-4 items-center justify-end">
          <Button type="submit" size="lg" :disabled="isPending">
            {{ t("submit") }}
          </Button>
          <Icon v-if="isPending" icon="Loading" />
        </div>
      </template>
    </Form>
  </Transition>
</template>
